/**
 * Premier PDP product gallery — per-slide zoom, mobile touch, infinite loop.
 */
(function () {
  const MOBILE_MQ = window.matchMedia('(max-width: 999px)');

  function imageCells(gallery) {
    const carousel = gallery.carousel;
    if (!carousel) return [];
    return carousel.cells.filter(
      (cell) =>
        cell.getAttribute('data-media-type') === 'image' && !cell.hasAttribute('data-wd-loop-clone')
    );
  }

  function syncZoomButtons(gallery) {
    const carousel = gallery.carousel;
    if (!carousel) return;

    const activeCell = carousel.selectedCell;
    gallery.querySelectorAll('[data-wd-premier-zoom]').forEach((btn) => {
      const cell = btn.closest('.product-gallery__media');
      const isImage = cell?.getAttribute('data-media-type') === 'image';
      const isClone = cell?.hasAttribute('data-wd-loop-clone');
      const isActive = cell === activeCell;
      const hidden = !isImage || !isActive || isClone;
      btn.classList.toggle('product-gallery__zoom-button--hidden', hidden);
      btn.toggleAttribute('hidden', hidden);
      btn.tabIndex = hidden ? -1 : 0;
    });
  }

  function bindZoomButtons(gallery) {
    gallery.querySelectorAll('[data-wd-premier-zoom]').forEach((btn) => {
      if (btn.dataset.wdPremierZoomBound === 'true') return;
      btn.dataset.wdPremierZoomBound = 'true';

      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const cell = btn.closest('.product-gallery__media');
        if (cell?.hasAttribute('data-wd-loop-clone')) return;

        const cells = imageCells(gallery);
        const index = cells.indexOf(cell);
        if (index < 0) return;

        gallery.dispatchEvent(
          new CustomEvent('lightbox:open', {
            bubbles: true,
            detail: { index },
          })
        );
      });
    });
  }

  function unblockMobileTouchScroll(gallery) {
    const carousel = gallery.carousel;
    if (!carousel || carousel.dataset.wdPremierTouchUnblock === 'true') return;
    carousel.dataset.wdPremierTouchUnblock = 'true';

    carousel.addEventListener(
      'gesturestart',
      (event) => {
        event.stopImmediatePropagation();
      },
      { capture: true }
    );
  }

  function uniquifySvgIds(root, suffix) {
    root.querySelectorAll('filter[id]').forEach((filterEl) => {
      const oldId = filterEl.id;
      const newId = `${oldId}-${suffix}`;
      filterEl.id = newId;
      root.querySelectorAll('[filter]').forEach((node) => {
        const value = node.getAttribute('filter');
        if (value && value.includes(`#${oldId}`)) {
          node.setAttribute('filter', value.replace(`#${oldId}`, `#${newId}`));
        }
      });
    });
  }

  function prepareLoopClone(clone, type) {
    clone.setAttribute('data-wd-loop-clone', type);
    clone.classList.remove('is-initial');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-media-id');
    clone.querySelectorAll('.wd-premier-pdp__zoom-slot').forEach((slot) => slot.remove());
    clone.querySelectorAll('video, video-media').forEach((el) => {
      if (typeof el.pause === 'function') el.pause();
    });
    uniquifySvgIds(clone, `loop-${type}`);
  }

  function keepArrowsEnabled(gallery) {
    const enable = () => {
      gallery
        .querySelectorAll('.wd-gallery-premier-nav, .product-gallery__premier-nav')
        .forEach((btn) => {
          btn.disabled = false;
        });
    };
    enable();
    gallery.carousel?.addEventListener('scroll:edge-nearing', enable, { capture: true });
    gallery.carousel?.addEventListener('scroll:edge-leaving', enable, { capture: true });
  }

  function syncThumbnailNav(gallery) {
    const carousel = gallery.carousel;
    const nav = gallery.querySelector('product-gallery-navigation');
    if (!carousel || !nav) return;

    const cell = carousel.selectedCell;
    if (!cell || cell.hasAttribute('data-wd-loop-clone')) return;

    const mediaId = cell.getAttribute('data-media-id');
    if (!mediaId) return;

    nav.querySelectorAll('button[data-media-id]').forEach((btn) => {
      btn.setAttribute('aria-current', btn.getAttribute('data-media-id') === mediaId ? 'true' : 'false');
    });
  }

  function bindThumbnailNav(gallery) {
    const nav = gallery.querySelector('product-gallery-navigation');
    const carousel = gallery.carousel;
    if (!nav || !carousel || nav.dataset.wdLoopNavBound === 'true') return;
    nav.dataset.wdLoopNavBound = 'true';

    nav.querySelectorAll('button[data-media-id]').forEach((btn) => {
      btn.addEventListener(
        'click',
        (event) => {
          const mediaId = btn.getAttribute('data-media-id');
          const target = carousel.cells.find(
            (cell) =>
              cell.getAttribute('data-media-id') === mediaId && !cell.hasAttribute('data-wd-loop-clone')
          );
          if (!target) return;

          event.stopPropagation();
          event.preventDefault();
          carousel.select(carousel.cells.indexOf(target));
          nav.querySelectorAll('button[data-media-id]').forEach((item) => {
            item.setAttribute('aria-current', item === btn ? 'true' : 'false');
          });
        },
        { capture: true }
      );
    });
  }

  function setupInfiniteLoop(gallery) {
    const carousel = gallery.carousel;
    if (!carousel || carousel.dataset.wdLoopInit === 'true') return;

    const originals = carousel.cells.filter((cell) => !cell.hasAttribute('data-wd-loop-clone'));
    if (originals.length < 2) return;

    carousel.dataset.wdLoopInit = 'true';

    const cloneStart = originals[originals.length - 1].cloneNode(true);
    const cloneEnd = originals[0].cloneNode(true);
    prepareLoopClone(cloneStart, 'start');
    prepareLoopClone(cloneEnd, 'end');

    carousel.insertBefore(cloneStart, originals[0]);
    carousel.appendChild(cloneEnd);

    const firstRealIndex = 1;
    const lastRealIndex = originals.length;
    carousel.dataset.wdLoopFirstReal = String(firstRealIndex);
    carousel.dataset.wdLoopLastReal = String(lastRealIndex);

    const initialCell = originals.find((cell) => cell.classList.contains('is-initial')) || originals[0];
    const initialIndex = firstRealIndex + originals.indexOf(initialCell);

    let loopJumping = false;

    const jumpTo = (index) => {
      loopJumping = true;
      carousel.select(index, { instant: true });
      syncZoomButtons(gallery);
      syncThumbnailNav(gallery);
    };

    carousel.addEventListener('carousel:settle', () => {
      if (loopJumping) {
        loopJumping = false;
        return;
      }
      const idx = carousel.selectedIndex;
      if (idx === 0) {
        jumpTo(lastRealIndex);
      } else if (idx === lastRealIndex + 1) {
        jumpTo(firstRealIndex);
      } else {
        syncThumbnailNav(gallery);
      }
    });

    keepArrowsEnabled(gallery);
    bindThumbnailNav(gallery);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        jumpTo(initialIndex);
      });
    });
  }

  function initGallery(gallery) {
    if (!gallery.hasAttribute('data-wd-premier-pdp')) return;
    if (gallery.dataset.wdPremierGalleryInit === 'true') return;
    gallery.dataset.wdPremierGalleryInit = 'true';

    const carousel = gallery.carousel;
    if (!carousel) return;

    if (MOBILE_MQ.matches) {
      unblockMobileTouchScroll(gallery);
    }
    MOBILE_MQ.addEventListener('change', (event) => {
      if (event.matches) {
        unblockMobileTouchScroll(gallery);
      }
    });

    bindZoomButtons(gallery);
    setupInfiniteLoop(gallery);

    const runSync = () => syncZoomButtons(gallery);
    runSync();

    carousel.addEventListener('carousel:select', runSync);
    carousel.addEventListener('carousel:change', () => {
      runSync();
      syncThumbnailNav(gallery);
    });
    carousel.addEventListener('carousel:settle', () => {
      window.requestAnimationFrame(runSync);
    });

    if (MOBILE_MQ.matches) {
      carousel.addEventListener('scroll', () => window.requestAnimationFrame(runSync), {
        passive: true,
      });
    }

    MOBILE_MQ.addEventListener('change', runSync);
  }

  function scan() {
    document.querySelectorAll('product-gallery[data-wd-premier-pdp]').forEach(initGallery);
  }

  function boot() {
    if (window.customElements?.get('product-gallery')) {
      scan();
      return;
    }
    window.customElements?.whenDefined('product-gallery').then(scan);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
